import dayjs from 'dayjs'
import {useQuery} from '@tanstack/react-query'
import {CreditPackBalances, CreditPackLot, CreditPackTerms, paymentsQueries} from '../../../services/payments'
import {BillingHistoryItem, workspaceQueries} from '../../../services/workspace'
import {formatNumber, formatRowDate, formatSentenceDate} from './CreditPackModal/format'

// 구독 종료와 크레딧 팩 잔액 고지 — 화면 전달본 P5(해지 모달) · P6(구독 종료 후 구독 관리).
// 약관 고지 의무(정책 16.4 · 16.5)라 문안은 글자 그대로다. {선택권 기간}은 판매 목록 terms 로 채운다.
//
// 노출 조건
//  - 선택권 기간이 서버에 설정돼 있어야 한다(없으면 숫자를 지어내지 않고 고지하지 않는다)
//  - 잔액을 믿을 수 있으면(available) 남은 결제 건이 있을 때만 고지 + 구매 건별 잔액 줄
//  - 잔액을 믿을 수 없으면(available=false, aivatar #542 이후 드문 경우) 소멸하지 않은 구매 건이 있을 때
//    고지 문단만 보이고 잔액 줄은 그리지 않는다. 고지 문단에는 잔액 숫자가 없어 잔액을 몰라도 틀리지 않고,
//    의무 고지가 빠지는 쪽보다 잔액 0 인 고객에게 한 번 더 안내되는 쪽이 낫다

/** 소멸하지 않은 구매 건을 찾을 청구 내역 범위. 팩 소멸 기한(현행 1년) 안의 행을 넉넉히 덮는다 */
export const LIVE_PURCHASE_LOOKUP_PARAMS = {page: 0, size: 100, sort: 'paidAt,desc'}

/** 청구 내역에 아직 소멸하지 않은(오늘이 사용 기한 이하인) 크레딧 팩 구매 건이 있는가 */
export function hasLiveCreditPackPurchase(rows: BillingHistoryItem[] | null | undefined) {
    const today = dayjs().tz('Asia/Seoul').format('YYYY-MM-DD')
    return (rows ?? []).some((row) => row.type === 'CREDIT_PACK' && row.expiresOn != null && row.expiresOn >= today)
}

export type TerminationNotice = {
    optionPeriodDays: number
    /** 구매 건별 잔액 줄. 잔액을 믿을 수 없으면 빈 배열 — 줄을 그리지 않는다 */
    lots: CreditPackLot[]
}

/**
 * 고지할지와 무엇을 보일지. available=false 일 때만 청구 내역(hasLivePurchase)을 본다 —
 * 그 전에는 undefined 로 두어 불필요한 조회를 하지 않는다
 */
export function terminationNoticeOf(
    balances: CreditPackBalances | null | undefined,
    terms: CreditPackTerms | null | undefined,
    hasLivePurchase?: boolean,
): TerminationNotice | null {
    const optionPeriodDays = terms?.optionPeriodDays
    if (optionPeriodDays == null || !balances) return null
    if (balances.available) {
        return balances.lots.length > 0 ? {optionPeriodDays, lots: balances.lots} : null
    }
    return hasLivePurchase ? {optionPeriodDays, lots: []} : null
}

function CommonParagraphs() {
    return (
        <>
            <p>잔액은 구매 건별 소멸일까지 그대로 남아 있고, 다시 구독하시면 이어서 사용할 수 있습니다.</p>
            <p>
                사용이 멈춘 기간에도 소멸일은 그대로 진행되므로, 다시 구독하지 않으시면 남은 크레딧은 소멸일 다음 날
                사라집니다.
            </p>
        </>
    )
}

/** P5 · 해지 모달의 크레딧 팩 고지. 해지 버튼과 같은 화면에 두고 접지 않는다. 환불 버튼 · 링크는 두지 않는다 */
export function CreditPackCancelNotice({optionPeriodDays}: { optionPeriodDays: number }) {
    return (
        <div className="bg-gray-6 text-gray-21 mt-[16px] flex max-w-[460px] flex-col gap-[6px] break-keep rounded-[8px] px-[16px] py-[14px] text-left text-[13px] leading-[1.6]">
            <p>보유하신 크레딧 팩 잔액은 구독이 끝나면 사용할 수 없습니다.</p>
            <CommonParagraphs/>
            <p>
                소멸일까지 언제든 고객센터로 환불을 신청하실 수 있고, 구독 종료일로부터 {optionPeriodDays}일 안에
                신청하시면 위약금 없이 미사용분 전액을 돌려드립니다. 이 기간에 다시 구독하시면 위약금 없는 환불은 적용되지
                않습니다.
            </p>
        </div>
    )
}

/**
 * 선택권 기간 안인가 — 구독 종료일을 0일로 세어 선택권 기간째 당일까지(정책 16.5 · D-67).
 * BE CreditPackRefundCalculator.withinDays 와 같은 계산이다.
 */
function isWithinOptionPeriod(terminatedOn: string, optionPeriodDays: number) {
    const today = dayjs(dayjs().tz('Asia/Seoul').format('YYYY-MM-DD'))
    // startedAt 에 시각이 붙어 와도 날짜 단위로 센다 — 시각이 남으면 diff 가 내림돼 하루 늦게 닫힌다
    const elapsed = today.diff(dayjs(dayjs(terminatedOn).format('YYYY-MM-DD')), 'day')
    return elapsed >= 0 && elapsed <= optionPeriodDays
}

/**
 * 선택권 기간을 셀 구독 종료일 — 잔액 응답의 terminatedOn(BE 환불 산정의 선택권 기산점과 같은 값)을 쓴다.
 * 현재 구독(PAUSE 행)의 startedAt 은 무료 승급이 회수된 워크스페이스에서 회수일이라 쓰면 안 된다(정책 10.4 · 16.6).
 * 응답에 필드가 없을 때(BE 배포 전)만 startedAt 으로 센다. null 이면 선택권 문단을 띄우지 않는다
 */
export function terminatedOnOf(balances: CreditPackBalances, subscriptionStartedAt: string): string | null {
    return balances.terminatedOn === undefined ? subscriptionStartedAt : balances.terminatedOn
}

type PausedNoticeProps = {
    workspaceId: number
    /** 현재 구독(PAUSE 행)의 startedAt — 잔액 응답에 terminatedOn 이 없을 때(BE 배포 전)만 쓰는 대체값 */
    subscriptionStartedAt: string
    className?: string
}

/** P6 · 구독 종료 후 구독 관리 화면의 크레딧 팩 안내와 구매 건별 잔액 */
export function CreditPackPausedNotice({workspaceId, subscriptionStartedAt, className}: PausedNoticeProps) {
    const {data: balances} = useQuery({
        ...paymentsQueries.creditPackBalances(workspaceId),
        enabled: Boolean(workspaceId),
    })
    const {data: catalog} = useQuery({
        ...paymentsQueries.creditPacks(workspaceId),
        enabled: Boolean(workspaceId),
    })
    // 잔액을 믿을 수 없을 때만 소멸하지 않은 구매 건이 있는지 청구 내역으로 본다
    const {data: history} = useQuery({
        ...workspaceQueries.detail(workspaceId)._ctx.billingHistory(LIVE_PURCHASE_LOOKUP_PARAMS),
        enabled: Boolean(workspaceId) && balances?.available === false,
    })

    const notice = terminationNoticeOf(balances, catalog?.terms, hasLiveCreditPackPurchase(history?.content))
    if (!notice) return null
    const {optionPeriodDays, lots} = notice

    const terminatedOn = balances ? terminatedOnOf(balances, subscriptionStartedAt) : null
    const optionDeadlineFrom = terminatedOn != null && isWithinOptionPeriod(terminatedOn, optionPeriodDays)
        ? terminatedOn
        : null

    return (
        <section
            className={`border-gray-4 text-gray-21 flex flex-col gap-[6px] break-keep rounded-[10px] border px-[24px] py-[20px] text-[13px] leading-[1.6] ${className ?? ''}`}
        >
            <p>구독이 종료되어 보유하신 크레딧 팩 잔액은 지금 사용할 수 없습니다.</p>
            <CommonParagraphs/>
            {optionDeadlineFrom ? (
                <p>
                    소멸일까지 언제든 고객센터로 환불을 신청하실 수 있고, {formatSentenceDate(optionDeadlineFrom)}로부터{' '}
                    {optionPeriodDays}일 안에 신청하시면 위약금 없이 미사용분 전액을 돌려드립니다. 이 기간에 다시
                    구독하시면 위약금 없는 환불은 적용되지 않습니다.
                </p>
            ) : (
                <p>소멸일까지 언제든 고객센터로 환불을 신청하실 수 있습니다.</p>
            )}

            {lots.length > 0 ? (
                <>
                    <h4 className="text-gray-2 mt-[12px] text-[14px] font-bold">구매 건별 잔액</h4>
                    <ul className="flex flex-col gap-[4px]">
                        {lots.map((lot) => (
                            <li key={lot.paymentOrderId} className="text-gray-2">
                                {formatRowDate(lot.purchasedOn)} 구매 · 남은 크레딧 {formatNumber(lot.remaining)} · 사용
                                기한 {formatRowDate(lot.expiresOn)}까지
                            </li>
                        ))}
                    </ul>
                </>
            ) : null}
        </section>
    )
}
